import { useState, useCallback, useContext, useEffect, useRef } from 'react';

// Firebase imports
import firebase from 'firebase/app';
import 'firebase/firestore';

// Extend the Window interface to include suppressBeforeUnload
declare global {
  interface Window {
    suppressBeforeUnload?: boolean;
  }
}
import {
  isSupported,
  LocalTrack,
  createLocalTracks,
  RemoteVideoTrack
} from 'twilio-video';
import { CSSProperties } from 'react';

// Material ui components
import { Typography, Button, Divider, Box } from '@material-ui/core';

// Mateirla UI Lab
import { Alert } from '@material-ui/lab';

// Authentication
import { CallContext, CallStatus } from 'services/call';

import useWindowSize from 'hooks/useWindowSize';

// Location hook
import { useLocation } from 'react-router-dom';

// So we can parse the success messages
import queryString from 'query-string';

// Component imports
import {
  Participant,
  Controls,
  LocalPreview,
  MaterialsPanel,
  Countdown,
  ScreenShare
} from './components';

// Booking info component
import { BookingInfo, Portlet } from 'components';

import { Status } from 'views/shared';

// Component styles
import useStyles from './styles';
import { AuthContext } from 'services/auth';
import { VirtualBackgroundProcessor } from '@twilio/video-processors';
import { TabProvider } from './components/TabContext';
import AudioContent from 'views/admin/MaterialInfo/components/CourseTable/AudioContent';
// import { TabHeader } from './components/TabHeader';

function getMediaErrorMessage(error: Error) {
  switch (error.name) {
    default:
      return 'An unexpected error occured';
    case 'NotFoundError':
      return `Please ensure you have at least one input device connected, and that its enabled in your browser's settings. `;
    case 'NotAllowedError':
      return `Please provide e-English with access to your camera/microphone in your browser's settings.`;
    case 'NotReadableError':
      return `Please close all other apps and tabs using`;
  }
}

function Call(): JSX.Element {
  // Get the token from the state
  const { token, room } = queryString.parse(useLocation().search);
  const call = useContext(CallContext);
  const auth = useContext(AuthContext);
  const classes = useStyles();
  const [previewTracks, setPreviewTracks] = useState<LocalTrack[] | undefined>(
    undefined
  );
  const [isFullScreen, setIsFullScreen] = useState(auth?.role === 'student');
  const [audioPaths, setAudioPaths] = useState([]);
  const [previewError, setPreviewError] = useState<Error | null>(null);
  const [resource, setResource] = useState<string | null>(null);

  const [remoteScreenShareTrack, setRemoteScreenShareTrack] =
    useState<RemoteVideoTrack | null>(null);

  const [virtualBackgroundProcessor, setVirtualBackgroundProcessor] =
    useState<VirtualBackgroundProcessor | null>(null);
  const [virtualBackgroundApplied, setVirtualBackgroundApplied] =
    useState<boolean>(false); // State to manage virtual background effect application
  const landscapeViewRef = useRef<HTMLDivElement | null>(null);
  const ControlbuttonViewRef = useRef<HTMLDivElement | null>(null);
  const [otherSectionHeight, setOtherSectionHeight] = useState<string | null>(
    null
  );
  const [fullScreenHeight, setFullScreenHeight] = useState<string | null>(null);
  const [booking, setBooking] = useState<any>(null);

  const [width] = useWindowSize();

  useEffect(() => {
    const updateHeight = () => {
      requestAnimationFrame(() => {
        if (landscapeViewRef.current && width < 900 && width > 500) {
          const landscapeHeight = landscapeViewRef.current?.offsetHeight;
          setOtherSectionHeight(`calc(100dvh - ${landscapeHeight}px)`);
        }
        console.log('Call component re-rendering in layout effect');
        if (ControlbuttonViewRef.current && width < 900 && width > 500) {
          setFullScreenHeight(`calc(100dvh - ${20}px)`);
        }
      });
    };

    updateHeight();
    window.addEventListener('resize', updateHeight);

    return () => {
      window.removeEventListener('resize', updateHeight);
    };
  }, [width]);

  const [, bookingId] = (room as string).split('-');

  const updateBooking = useCallback(
    async (newBooking: any, prevBooking: any) => {
      try {
        console.log('Updating booking with new info:', newBooking, prevBooking);

        if (bookingId && auth?.user) {
          // Get tutorId from localStorage
          const bookingStorage = localStorage.getItem(`call-${bookingId}`);
          if (!bookingStorage) {
            console.error('No booking data found in localStorage');
            return;
          }

          const localBooking = JSON.parse(bookingStorage);
          const tutorId = localBooking.tutor;

          if (!tutorId) {
            console.error('No tutor ID found in booking data');
            return;
          }

          const db = firebase.firestore();
          const bookingRef = db
            .collection('tutors')
            .doc(tutorId)
            .collection('bookings')
            .doc(bookingId);

          // Update Firebase with the new booking info
          await bookingRef.update({
            info: newBooking,
            lastUpdated:
              typeof prevBooking?.lastUpdated === 'boolean'
                ? !prevBooking.lastUpdated
                : true,
            updatedBy: auth.user.uid
          });

          console.log('Successfully updated booking in Firebase', prevBooking);
        }
      } catch (error) {
        console.error('Error updating booking:', error);
      }
    },
    [bookingId, auth?.user]
  );

  useEffect(() => {
    const findRemoteScreenShareTrack = () => {
      const track = call.participants
        .flatMap((participant) => Array.from(participant.videoTracks.values()))
        .find(
          (trackPublication) => trackPublication.track?.name === 'screen-share'
        )?.track as RemoteVideoTrack;

      setRemoteScreenShareTrack(track || null);
    };

    findRemoteScreenShareTrack();

    call.participants.forEach((participant) => {
      participant.on('trackSubscribed', findRemoteScreenShareTrack);
      participant.on('trackUnsubscribed', findRemoteScreenShareTrack);
      participant.on('trackSubscribed', (track) => {
        if (track.kind === 'audio') {
          const audioEl = track.attach();
          audioEl.autoplay = true;
          document.body.appendChild(audioEl); // or a hidden div
        }
      });
    });

    return () => {
      call.participants.forEach((participant) => {
        participant.off('trackSubscribed', findRemoteScreenShareTrack);
        participant.off('trackUnsubscribed', findRemoteScreenShareTrack);
      });
    };
  }, [call.localParticipant, call.participants, call.screenSharing]);
  const leaveCall = useCallback(() => {
    if (call.leave && call.connected) {
      call.leave();
      if (bookingId) {
        localStorage.removeItem(`call-${bookingId}`);
        localStorage.removeItem(`joined-${bookingId}`); // Clear the joined state
      }
    }
  }, [bookingId, call]);

  useEffect(() => {
    let visibilityTimer: NodeJS.Timeout;

    const handleVisibilityChange = () => {
      if (
        document.visibilityState === 'hidden' &&
        call.connected &&
        !window.suppressBeforeUnload
      ) {
        // Don't immediately leave - give user time to return
        visibilityTimer = setTimeout(() => {
          if (document.visibilityState === 'hidden' && call.connected) {
            console.log('User has been away for too long, leaving call');
          }
        }, 60000); // Wait 60 seconds before leaving call
      } else if (document.visibilityState === 'visible') {
        // User returned, cancel the timer
        if (visibilityTimer) {
          clearTimeout(visibilityTimer);
        }
      }
    };

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // This won't work on iOS, but will work on desktop browsers
      if (!window.suppressBeforeUnload && call.connected) {
        event.preventDefault();
        event.returnValue = 'Are you sure you want to leave the call?';
        return 'Are you sure you want to leave the call?';
      }
    };

    const handlePageHide = () => {
      console.log('Page is being hidden, leaving call');
    };

    // Detect iOS devices
    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    if (
      call.connected ||
      (booking?.when && new Date(booking.when) > new Date())
    ) {
      document.addEventListener('visibilitychange', handleVisibilityChange);
      window.addEventListener('beforeunload', handleBeforeUnload);
      window.addEventListener('pagehide', handlePageHide);

      // iOS-specific handling
      if (isIOS) {
        // On iOS, also listen for focus/blur as backup
        const handleBlur = () => {
          if (call.connected && !window.suppressBeforeUnload) {
            // Set a shorter timer for iOS blur events
            visibilityTimer = setTimeout(() => {
              if (document.visibilityState === 'hidden' && call.connected) {
                console.log('User has been away for too long, leaving call');
              }
            }, 30000); // 30 seconds for iOS
          }
        };

        const handleFocus = () => {
          if (visibilityTimer) {
            clearTimeout(visibilityTimer);
          }
        };

        window.addEventListener('blur', handleBlur);
        window.addEventListener('focus', handleFocus);

        return () => {
          document.removeEventListener(
            'visibilitychange',
            handleVisibilityChange
          );
          window.removeEventListener('beforeunload', handleBeforeUnload);
          window.removeEventListener('pagehide', handlePageHide);
          window.removeEventListener('blur', handleBlur);
          window.removeEventListener('focus', handleFocus);
          if (visibilityTimer) {
            clearTimeout(visibilityTimer);
          }
        };
      }
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
      if (visibilityTimer) {
        clearTimeout(visibilityTimer);
      }
    };
  }, [call.connected, booking?.when, bookingId, leaveCall]);

  // Simplified iOS refresh detection (more reliable)
  useEffect(() => {
    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    if (!isIOS || !call.connected) return;

    let refreshWarningShown = false;

    const handleTouchStart = (e: TouchEvent) => {
      // Only check for pull-to-refresh when at top of page
      if (window.pageYOffset === 0 && e.touches.length === 1) {
        const startY = e.touches[0].clientY;
        let hasWarned = false;

        const handleTouchMove = (moveEvent: TouchEvent) => {
          const currentY = moveEvent.touches[0].clientY;
          const deltaY = currentY - startY;

          // If pulling down significantly and haven't warned yet
          if (deltaY > 80 && !hasWarned && !refreshWarningShown) {
            hasWarned = true;
            refreshWarningShown = true;

            // Try to prevent the refresh and show warning
            moveEvent.preventDefault();

            const shouldRefresh = confirm(
              'Refreshing the page will end your call. Are you sure you want to continue?'
            );

            if (shouldRefresh) {
              window.suppressBeforeUnload = true;

              // Allow the refresh to proceed
              setTimeout(() => {
                window.location.reload();
              }, 100);
            } else {
              // Reset the warning flag after a delay
              setTimeout(() => {
                refreshWarningShown = false;
              }, 2000);
            }
          }
        };

        const handleTouchEnd = () => {
          document.removeEventListener('touchmove', handleTouchMove);
          document.removeEventListener('touchend', handleTouchEnd);
          // Reset warning flag when touch ends
          setTimeout(() => {
            refreshWarningShown = false;
          }, 1000);
        };

        document.addEventListener('touchmove', handleTouchMove, {
          passive: false
        });
        document.addEventListener('touchend', handleTouchEnd);
      }
    };

    document.addEventListener('touchstart', handleTouchStart, {
      passive: true
    });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
    };
  }, [call.connected, leaveCall]);
  useEffect(() => {
    async function setupLocalPreview() {
      try {
        const tracks = await createLocalTracks();
        setPreviewTracks(tracks);
      } catch (error) {
        console.error('Could not create local tracks!', error);
        setPreviewError(error as Error);
      }
    }

    setupLocalPreview();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [call.connected, booking?.when]);

  const onDisconnect = useCallback(() => {
    const confirmed = window.confirm(
      'Are you sure you want to leave the call?'
    );

    if (confirmed) {
      // Set a flag to suppress the beforeunload alert
      window.suppressBeforeUnload = true;
      leaveCall();
      setTimeout(() => {
        window.close();
      }, 0); // Delay to ensure cleanup happens before closing
    } else {
      window.suppressBeforeUnload = false;
    }
  }, [leaveCall]);

  const joinRoom = useCallback(() => {
    if (isSupported && call.join && token && room) {
      if (!call.connected)
        call.join(token as string, room as string, previewTracks);
      localStorage.setItem(`joined-${bookingId}`, 'true');

      auth?.api.chat // Trigger a message for joiner
        .send(bookingId, `${auth?.user?.displayName} Joined the call`);
    }

    return () => {
      if (call.leave) call.leave();
      if (!call.error) {
        window.close();
        if (bookingId) localStorage.removeItem(`call-${bookingId}`);
      }
    };
  }, [call, token, room, previewTracks, bookingId, auth]);
  useEffect(() => {
    const hasJoined = localStorage.getItem(`joined-${bookingId}`) === 'true';

    if (hasJoined && isSupported && call.join && token && room) {
      if (!call.connected) {
        joinRoom();
      }
    }
  }, []);
  // when teacher share screen setfullscreen true for student
  useEffect(() => {
    if (remoteScreenShareTrack && auth?.role === 'student') {
      setIsFullScreen(true);
    }
  }, [remoteScreenShareTrack, auth?.role]);
  // Check for booking in local storage and set up Firebase listener
  useEffect(() => {
    let unsubscribe: (() => void) | null = null;

    if (bookingId && auth?.user) {
      console.log('Setting up booking data for booking:', bookingId);

      // Get tutorId from localStorage
      const bookingStorage = localStorage.getItem(`call-${bookingId}`);
      if (!bookingStorage) {
        console.error('No booking data found in localStorage');
        return;
      }

      const localBooking = JSON.parse(bookingStorage);

      // For group bookings, only use localStorage data
      if (localBooking.type === 'group') {
        console.log('Group booking detected, using localStorage data only');
        setBooking(localBooking);
        return;
      }

      const tutorId = localBooking.tutor;

      if (!tutorId) {
        console.error('No tutor ID found in booking data');
        return;
      }

      const db = firebase.firestore();
      const bookingRef = db
        .collection('tutors')
        .doc(tutorId)
        .collection('bookings')
        .doc(bookingId);

      // Set up real-time listener only for non-group bookings
      unsubscribe = bookingRef.onSnapshot(
        (doc) => {
          if (doc.exists) {
            const data = doc.data();
            const firebaseBooking = {
              id: doc.id,
              ...data,
              lastUpdated: data?.lastUpdated ?? false
            };

            console.log('Firebase booking update received:', firebaseBooking);

            console.log('Updated by:', data?.updatedBy);
            setBooking({ ...firebaseBooking });
          }
        },
        (error) => {
          console.error('Error listening to booking changes:', error);
        }
      );
    }

    // Cleanup function
    return () => {
      if (unsubscribe) {
        console.log('Cleaning up Firebase booking listener');
        unsubscribe();
      }
    };
  }, [bookingId, auth?.user]);

  const handlePlay = async (playid: string) => {
    if (call) {
      if (call?.startAudioShare) {
        await call.startAudioShare(playid);
      }
    }
  };
  console.log({ booking });
  interface AudioRef {
    current?: HTMLAudioElement | null;
  }

  const handlePause = (audioRef: AudioRef): void => {
    audioRef.current?.pause();
  };

  if (!isSupported) {
    return (
      <Status
        title="Browser does not support video calling"
        message="We require a recent version of Chrome and Firefox, or Safari 11 and above."
      />
    );
  } else if (call.error) {
    return <Status title="Video Calling Error" message={call.error.message} />;
  } else if (previewError) {
    return (
      <Status
        title="Camera / Mic Error"
        message={getMediaErrorMessage(previewError)}
      />
    );
  } else if (!token) {
    return (
      <Status
        title="Joining Error"
        message="A video call token was not supplied."
      />
    );
  } else if (!room) {
    return (
      <Status title="Joining Error" message="A room code was not supplied." />
    );
  } else if (call.status === CallStatus.Disconnected) {
    return (
      <div className={classes.joiningRoot}>
        <Typography variant="h2">Looking good!</Typography>
        <Typography variant="subtitle1" color="textSecondary">
          Press the join lesson button when you&apos;re ready.
        </Typography>
        <LocalPreview previewTracks={previewTracks} />
        <Button
          disabled={!previewTracks || previewTracks.length === 0}
          size="large"
          variant="contained"
          onClick={joinRoom}>
          Join lesson
        </Button>
      </div>
    );
  } else if (call.status === CallStatus.Connecting) {
    return (
      <div className={classes.joiningRoot}>
        <Typography variant="h2" color="textSecondary">
          Joining call...
        </Typography>
      </div>
    );
  }

  window.addEventListener('resize', () => {
    // We execute the same script as before
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  });

  // Render either the materials view, or the normal view

  const rowReverse: CSSProperties =
    width >= 500 && width <= 900 ? { flexDirection: 'row-reverse' } : {};

  return (
    <TabProvider>
      <div className={classes.root}>
        <div className={classes.main}>
          <div
            className={
              width > 900
                ? classes.materialsContainer
                : classes.materialsContainerResponsive
            }>
            <MaterialsPanel
              key={`materials-${bookingId}-${booking?.lastUpdated}`}
              path={resource}
              info={booking?.info || booking?.bookings[0]?.info}
              call={call}
              booking={booking}
              setAudioPaths={setAudioPaths}
              otherSectionHeight={otherSectionHeight}
            />
          </div>

          {width > 900 && (
            <div
              className={classes.sidebar}
              style={{
                overflowY:
                  isFullScreen && remoteScreenShareTrack ? 'hidden' : 'auto'
              }}>
              <Alert className={classes.tip} severity="info">
                <b>TIP:</b> If your internet connection is slow, turn off your
                video or ask your tutor to turn off their video to keep audio.
              </Alert>

              {/* <TabHeader tabs={tabs} /> */}

              <Portlet height="auto">
                <BookingInfo
                  info={booking?.info || booking?.bookings[0]?.info}
                  groupSession={booking?.type === 'group'}
                  booking={{ id: bookingId, ...booking }}
                  onResource={(path: string) => {
                    console.log('resource', path);
                    setResource(path);
                  }}
                  onBookingUpdate={(info: any) => {
                    console.log({ info });
                    updateBooking(info, booking);
                  }}
                  isCallComp={true}
                  hideOpenButton
                />
              </Portlet>
              {auth?.role === 'tutor' ? (
                <Box className={classes.audioContent}>
                  <AudioContent
                    key={`${booking?.lastUpdated}`}
                    audioPaths={audioPaths}
                    auth={auth}
                    booking={booking}
                    handlePlay={handlePlay}
                    handlePause={handlePause}
                  />
                </Box>
              ) : null}
              <Countdown
                when={booking?.when}
                onFinished={() => {
                  leaveCall();
                  window.close();
                }}
              />
              <Divider className={classes.divider} />
              {call.screenTrack && (
                <ScreenShare
                  screenShareTrack={call.screenTrack}
                  istTutor={true}
                  isFullScreen={isFullScreen}
                  setIsFullScreen={setIsFullScreen}
                  fullScreenHeight={fullScreenHeight}
                />
              )}
              {remoteScreenShareTrack && (
                <ScreenShare
                  screenShareTrack={remoteScreenShareTrack}
                  istTutor={false}
                  isFullScreen={isFullScreen}
                  setIsFullScreen={setIsFullScreen}
                  fullScreenHeight={fullScreenHeight}
                />
              )}
              {call.connected && call.localParticipant && (
                <div className="participant">
                  <Participant
                    isLocal
                    key={call.localParticipant.sid}
                    participant={call.localParticipant}
                    muted={true}
                    virtualBackgroundProcessor={virtualBackgroundProcessor}
                    setVirtualBackgroundProcessor={
                      setVirtualBackgroundProcessor
                    }
                    virtualBackgroundApplied={virtualBackgroundApplied}
                    setVirtualBackgroundApplied={setVirtualBackgroundApplied}
                  />
                </div>
              )}
              {call.participants.map((participant) => (
                <div
                  className="participant"
                  style={{ marginLeft: width < 900 ? 20 : 0 }}
                  key={participant.sid}>
                  <Participant
                    participant={participant}
                    muted={false}
                    virtualBackgroundProcessor={virtualBackgroundProcessor}
                    setVirtualBackgroundProcessor={
                      setVirtualBackgroundProcessor
                    }
                    virtualBackgroundApplied={virtualBackgroundApplied}
                    setVirtualBackgroundApplied={setVirtualBackgroundApplied}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
        {width < 500 && (
          <div className={classes.bottombar}>
            <Countdown
              when={booking?.when}
              onFinished={() => {
                leaveCall();
                window.close();
              }}
            />
            <div
              className={classes.participants}
              style={{
                flexDirection: 'row-reverse',
                overflowX: isFullScreen ? 'hidden' : 'auto'
              }}>
              {call.connected && call.localParticipant && (
                <div
                  className="participant"
                  style={{
                    marginRight: 'auto'
                  }}>
                  <Participant
                    isLocal
                    key={call.localParticipant.sid}
                    participant={call.localParticipant}
                    muted={true}
                    virtualBackgroundProcessor={virtualBackgroundProcessor}
                    setVirtualBackgroundProcessor={
                      setVirtualBackgroundProcessor
                    }
                    virtualBackgroundApplied={virtualBackgroundApplied}
                    setVirtualBackgroundApplied={setVirtualBackgroundApplied}
                  />
                </div>
              )}

              {call?.connected &&
                call?.participants?.map((participant) => (
                  <div className="participant" key={participant.sid}>
                    <Participant
                      participant={participant}
                      muted={false}
                      virtualBackgroundProcessor={virtualBackgroundProcessor}
                      setVirtualBackgroundProcessor={
                        setVirtualBackgroundProcessor
                      }
                      virtualBackgroundApplied={virtualBackgroundApplied}
                      setVirtualBackgroundApplied={setVirtualBackgroundApplied}
                    />
                  </div>
                ))}

              {call.screenTrack && (
                <ScreenShare
                  screenShareTrack={call.screenTrack}
                  istTutor={true}
                  isFullScreen={isFullScreen}
                  setIsFullScreen={setIsFullScreen}
                  fullScreenHeight={fullScreenHeight}
                />
              )}
              {remoteScreenShareTrack && (
                <ScreenShare
                  screenShareTrack={remoteScreenShareTrack}
                  istTutor={false}
                  isFullScreen={isFullScreen}
                  setIsFullScreen={setIsFullScreen}
                  fullScreenHeight={fullScreenHeight}
                />
              )}
              {auth?.role === 'tutor' ? (
                <div>
                  <Box className={classes.audioContent}>
                    <div
                      style={{
                        width: '300px'
                      }}>
                      <AudioContent
                        key={`${booking?.lastUpdated}`}
                        booking={booking}
                        audioPaths={audioPaths}
                        auth={auth}
                        handlePlay={handlePlay}
                        handlePause={handlePause}
                      />
                    </div>
                  </Box>
                </div>
              ) : null}
            </div>
          </div>
        )}
        {width > 500 && width < 900 ? (
          <div className={classes.landscapView} ref={landscapeViewRef}>
            <div className={classes.controls}>
              <Controls
                bookingId={bookingId}
                onDisconnect={onDisconnect}
                isFullScreen={isFullScreen}
                landscapeViewRef={ControlbuttonViewRef}
              />
              <Countdown
                when={booking?.when}
                onFinished={() => {
                  leaveCall();
                  window.close();
                }}
              />
            </div>
            <div className={classes.bottombarLandscape} style={rowReverse}>
              {call.connected && call.localParticipant && (
                <div className="participant">
                  <Participant
                    isLocal
                    key={call.localParticipant.sid}
                    participant={call.localParticipant}
                    muted={true}
                    virtualBackgroundProcessor={virtualBackgroundProcessor}
                    setVirtualBackgroundProcessor={
                      setVirtualBackgroundProcessor
                    }
                    virtualBackgroundApplied={virtualBackgroundApplied}
                    setVirtualBackgroundApplied={setVirtualBackgroundApplied}
                  />
                </div>
              )}

              {call.participants.map((participant) => (
                <div className="participant" key={participant.sid}>
                  <Participant
                    participant={participant}
                    muted={false}
                    virtualBackgroundProcessor={virtualBackgroundProcessor}
                    setVirtualBackgroundProcessor={
                      setVirtualBackgroundProcessor
                    }
                    virtualBackgroundApplied={virtualBackgroundApplied}
                    setVirtualBackgroundApplied={setVirtualBackgroundApplied}
                  />
                </div>
              ))}
              {call.screenTrack && (
                <ScreenShare
                  screenShareTrack={call.screenTrack}
                  istTutor={true}
                  isFullScreen={isFullScreen}
                  setIsFullScreen={setIsFullScreen}
                  fullScreenHeight={fullScreenHeight}
                />
              )}
              {remoteScreenShareTrack && (
                <ScreenShare
                  screenShareTrack={remoteScreenShareTrack}
                  istTutor={false}
                  isFullScreen={isFullScreen}
                  setIsFullScreen={setIsFullScreen}
                  fullScreenHeight={fullScreenHeight}
                />
              )}
              {auth?.role === 'tutor' ? (
                <div>
                  <Box className={classes.audioContent}>
                    <div
                      style={{
                        width: '300px'
                      }}>
                      <AudioContent
                        key={`${booking?.lastUpdated}`}
                        booking={booking}
                        audioPaths={audioPaths}
                        auth={auth}
                        handlePlay={handlePlay}
                        handlePause={handlePause}
                      />
                    </div>
                  </Box>
                </div>
              ) : null}
            </div>
          </div>
        ) : (
          <Controls
            bookingId={bookingId}
            onDisconnect={onDisconnect}
            isFullScreen={isFullScreen}
          />
        )}
      </div>
    </TabProvider>
  );
}

export default Call;
